import { 
  Stack, Paper, Grid, Typography, Box, Tabs, Tab, useMediaQuery, useTheme 
} from '@mui/material';
import { useEffect, useState } from 'react';
import { LoadingMember } from './LoadingMember';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

const monthNames = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", 
                   "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];

export function SupportCalendar(props) {
  const [record, setRecord] = useState([]);
  const [years, setYears] = useState([]);
  const [activeYear, setActiveYear] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [startYear, setStartYear] = useState(2021);
  const [startMonth, setStartMonth] = useState(11); // November (0-indexed for December)
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [maxFutureYear, setMaxFutureYear] = useState(currentYear);
  const [maxFutureMonth, setMaxFutureMonth] = useState(currentMonth);

  const theme = useTheme();
  // Detect if screen is below "sm" breakpoint (mobile)
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    setIsLoading(true);
    if (props?.data?.transactions) {
      parseData(props?.data?.transactions);
      populateYears();
    }
    setIsLoading(false);
  }, [props]);

  const populateYears = () => {
    let y = [];
    // Find the maximum year from transactions
    const maxYearFromTransactions = record.reduce((max, item) => {
      return item.year > max ? item.year : max;
    }, currentYear);
    
    const finalMaxYear = Math.max(currentYear, maxYearFromTransactions);
    
    for (let i = startYear; i <= finalMaxYear; i++) {
      y.push(i);
    }
    setYears(y);
    setActiveYear(finalMaxYear);
  };

  const parseData = (data) => {
    let thisData = data.map(d => {
      let y = new Date(d.valid_thru).getFullYear(),
          m = new Date(d.valid_thru).getMonth();
      return {
       'monthIndex': m,
       'year': y,
      }
    });
    
    // Find the maximum future date from transactions
    if (thisData.length > 0) {
      const maxTransaction = thisData.reduce((max, item) => {
        if (item.year > max.year || (item.year === max.year && item.monthIndex > max.monthIndex)) {
          return item;
        }
        return max;
      });
      
      setMaxFutureYear(maxTransaction.year);
      setMaxFutureMonth(maxTransaction.monthIndex);
    }
    
    setRecord(thisData);
  };

  const handleYearChange = (event, newValue) => {
    setActiveYear(newValue);
  };

  const getActiveMonths = () => {
    return record
      .filter(a => a.year === activeYear)
      .map(a => a.monthIndex);
  };

  const isMonthInRange = (year, monthIndex) => {
    // Check if before start date
    if (year < startYear || (year === startYear && monthIndex < startMonth)) {
      return false;
    }
    // Check if after max future date
    if (year > maxFutureYear || (year === maxFutureYear && monthIndex > maxFutureMonth)) {
      return false;
    }
    return true;
  };

  const YearsTabs = () => {
    return (
      <Box>
        <Tabs
        value={activeYear}
        onChange={handleYearChange}
        variant={isMobile ? 'scrollable' : 'fullWidth'}
        scrollButtons
        allowScrollButtonsMobile
        sx={{
           '& .MuiTabs-scrollButtons': {
            color: '#ffffff', 
          },
          '& .MuiTabs-indicator': {
            backgroundColor: '#F59E0B',
            height: '100%',
            opacity: 0.3,
            color: '#ffffff',
            fontSize: '14px',
          },
          mb: 3,
          background: 'linear-gradient(to right, #B91C1C, #530D0D)',
          borderTopLeftRadius: '1.5rem',
          borderTopRightRadius: '1.5rem',
        }}
      >
        {years.map((year) => (
          <Tab
            key={year}
            value={year}
            label={year}
            sx={{
              fontSize: '18px',
              fontWeight: 'bold',
              color: activeYear === year ? '#fff' : '#fff',
              backgroundColor: activeYear === year ? '#F59E0B' : 'transparent',
              borderRadius: '10px',
              m: 1.5,
              py: 1.5,
              '&.Mui-selected': {
                color: '#fff',
              },
            }}
          />
        ))}
      </Tabs>
      </Box>
    );
  };

  const MonthsGrid = () => {
    const activeMonths = getActiveMonths();
    
    return (
      <Grid container spacing={1} sx={{ p: 3, width: '100%' }} >
        {monthNames.map((month, index) => {
          const isActive = activeMonths.includes(index);
          const isInRange = isMonthInRange(activeYear, index);
          const isFuture = activeYear > currentYear || 
                          (activeYear === currentYear && index > currentMonth);
          
          return (
            <Grid size={{ xs: 4, sm: 4, md: 3 }} key={index}                 
              sx={{
                  p: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  height: '100%',
                  minHeight: '48px',
                  backgroundColor: !isInRange ? '#f5f5f5' : 'inherit',
                  opacity: !isInRange ? 0.7 : 1
                }}>
              
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  minHeight: '24px'
                }}>
                  {isInRange ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                      {isActive ? (
                        <CheckCircleIcon sx={{ color: '#52C41A', fontSize: '1.2rem' }} />
                      ) : (
                        <CancelIcon sx={{ 
                          color: isFuture ? '#999' : '#F5222D', 
                          fontSize: '1.2rem' 
                        }} />
                      )}
                      <Typography variant="h6" sx={{ fontWeight: '500' }}>
                        {month}
                      </Typography>
                    </Stack>
                  ) : (
                    <Typography variant="h6" sx={{ 
                      fontWeight: '500', 
                      color: '#999',
                    }}>
                      {month}
                    </Typography>
                  )}
                </Box>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  return (
    <Box sx={{ 
      width: '100%',
      mx: 'auto',
      borderTopLeftRadius: '1.5rem',
      borderTopRightRadius: '1.5rem',
    }}>
      {isLoading ? (
        <LoadingMember />
      ) : (
        <div style={{ width: '100%' }}>
          <YearsTabs />
          <MonthsGrid />
        </div>
      )}
    </Box>
  );
}